import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguages } from '../../hooks/useLanguages';
import { loadPhrasebookLanguageItems } from '../../services/basicPhrasesV2';
import LanguageFlag from '../../components/LanguageFlag';
import { speakPhrase } from './phrasebookShared';

// All ~80 basic-phrases-sentences entries rendered in a single chosen
// language, grouped by that language's own category text — the
// complementary "browse by language" mode to PhrasebookPage's default
// "browse by phrase" view. Mirrors PhrasebookLanguageScreen in the mobile
// app (fetches its own data independently, usable as a direct link).
export default function PhrasebookLanguagePage() {
  const { t } = useTranslation();
  const { code } = useParams();
  const { languages } = useLanguages();
  const lang = languages.find((l) => l.code === code) || { code, label: code };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    loadPhrasebookLanguageItems(code)
      .then((data) => { if (active) { setItems(data); setLoading(false); } })
      .catch(() => { if (active) { setError(true); setLoading(false); } });
    return () => { active = false; };
  }, [code]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${lang.nativeLabel || lang.label} | ${t('phrasebookPage.title')} | SublingoX`;
    return () => { document.title = previousTitle; };
  }, [lang.label, lang.nativeLabel, t]);

  let currentCategory = null;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <main className="mx-auto max-w-2xl">
        <Link to="/phrasebook" className="inline-flex items-center gap-2 text-sm font-black text-indigo-700 hover:text-indigo-900">
          <span aria-hidden="true">←</span> {t('phrasebookPage.title')}
        </Link>

        <header className="mt-4 mb-6 flex items-center gap-3">
          <LanguageFlag flag={lang.flag} label={lang.label} className="h-7 w-10" />
          <h1 className="text-2xl font-black text-slate-900">{lang.nativeLabel || lang.label}</h1>
        </header>

        {loading && (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((item) => <div key={item} className="h-14 animate-pulse rounded-2xl bg-slate-100" />)}
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <span className="text-5xl">🌐</span>
            <p className="mt-3 text-sm font-semibold text-slate-600">{t('phrasebookPage.unavailable')}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-1.5">
            {items.map((item) => {
              const showHeader = item.category && item.category !== currentCategory;
              if (showHeader) currentCategory = item.category;
              if (!item.text) return null;
              return (
                <div key={item.key}>
                  {showHeader && (
                    <h2 className="mb-1.5 mt-5 text-xs font-black uppercase tracking-wide text-slate-400 first:mt-0">
                      {item.category}
                    </h2>
                  )}
                  <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                    <p dir={lang.dir || 'ltr'} className="text-base font-bold text-slate-900">{item.text}</p>
                    <button
                      type="button"
                      onClick={() => speakPhrase(lang.tts, item.text)}
                      aria-label={t('phrasebookPage.listen')}
                      className="shrink-0 rounded-full p-1.5 text-indigo-600 hover:bg-indigo-50"
                    >
                      🔊
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
